import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, timeout } from "rxjs/operators";

import { IpInfo } from "../models/ip-info.model";

/**
 * IPinfo uses separate API hosts by IP family:
 *   IPv4 → ipinfo.io
 *   IPv6 → v6.ipinfo.io
 * Browsers do not expose a "prefers IPv6" flag. IPv6-only (or broken-IPv4)
 * clients hang or 504 on ipinfo.io, so we try IPv4 first and fall back.
 *
 * Auth is the token query param (not Authorization). A Bearer header would
 * force a CORS OPTIONS preflight; some corporate proxies 504 that OPTIONS.
 */
@Injectable({
  providedIn: "root",
})
export class IpGeoLocationService {
  static readonly ipv4Url = "https://ipinfo.io/json";
  static readonly ipv6Url = "https://v6.ipinfo.io/json";
  static readonly requestTimeoutMs = 5000;
  stateMapping = {
    Alabama: "AL",
    Alaska: "AK",
    Arizona: "AZ",
    Arkansas: "AR",
    California: "CA",
    Colorado: "CO",
    Connecticut: "CT",
    Delaware: "DE",
    Florida: "FL",
    Georgia: "GA",
    Hawaii: "HI",
    Idaho: "ID",
    Illinois: "IL",
    Indiana: "IN",
    Iowa: "IA",
    Kansas: "KS",
    Kentucky: "KY",
    Louisiana: "LA",
    Maine: "ME",
    Maryland: "MD",
    Massachusetts: "MA",
    Michigan: "MI",
    Minnesota: "MN",
    Mississippi: "MS",
    Missouri: "MO",
    Montana: "MT",
    Nebraska: "NE",
    Nevada: "NV",
    "New Hampshire": "NH",
    "New Jersey": "NJ",
    "New Mexico": "NM",
    "New York": "NY",
    "North Carolina": "NC",
    "North Dakota": "ND",
    Ohio: "OH",
    Oklahoma: "OK",
    Oregon: "OR",
    Pennsylvania: "PA",
    "Rhode Island": "RI",
    "South Carolina": "SC",
    "South Dakota": "SD",
    Tennessee: "TN",
    Texas: "TX",
    Utah: "UT",
    Vermont: "VT",
    Virginia: "VA",
    Washington: "WA",
    "West Virginia": "WV",
    Wisconsin: "WI",
    Wyoming: "WY",
  };

  constructor(private httpClient: HttpClient) {}

  getUserLocation(): Observable<IpInfo> {
    return this.lookup(IpGeoLocationService.ipv4Url).pipe(catchError(() => this.lookup(IpGeoLocationService.ipv6Url)));
  }

  private lookup(url: string): Observable<IpInfo> {
    return this.httpClient
      .get<IpInfo>(url, { params: { token: "23ff9d07e6248e" } })
      .pipe(timeout(IpGeoLocationService.requestTimeoutMs));
  }

  getStateName(stateCode: string) {
    for (const [state, code] of Object.entries(this.stateMapping)) {
      if (code === stateCode) {
        return state;
      }
    }
    return null;
  }

  getStateCode(state: string) {
    try {
      return this.stateMapping[state];
    } catch (e) {
      return state;
    }
  }
}
